import { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

// A {{ ... }} token (single brace pair, no nested braces). Matches the variable
// convention documented for prompt templates: {{input}} and
// {{steps.<id>.outputs.<key>}}.
const VAR_RE = /\{\{[^{}]*\}\}/g;

/** One selectable variable suggestion. */
export interface PromptVariable {
  value: string;
  label: string;
  hint?: string;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Render the text as HTML with {{variables}} wrapped in a green pill. Non-token
// text keeps the normal colour so only variables stand out.
function toHighlightedHtml(value: string): string {
  let out = "";
  let last = 0;
  for (const m of value.matchAll(VAR_RE)) {
    const idx = m.index ?? 0;
    out += escapeHtml(value.slice(last, idx));
    out += `<span class="rounded-[3px] bg-green-500/15 text-green-600 dark:text-green-400">${escapeHtml(m[0])}</span>`;
    last = idx + m[0].length;
  }
  out += escapeHtml(value.slice(last));
  // Trailing newline keeps the backdrop's height in step with the textarea
  // when the text ends on a newline.
  return out + "\n";
}

// If the caret sits inside an unclosed `{{ … `, return where it opened and what
// has been typed since, so we can offer variable suggestions.
function findVarContext(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const open = before.lastIndexOf("{{");
  if (open === -1) return null;
  const between = before.slice(open + 2);
  if (between.includes("}}")) return null; // already a completed token
  return { start: open, query: between };
}

/**
 * Textarea that highlights {{variable}} tokens in green and, when `variables`
 * are supplied, offers a suggestion dropdown as the user types `{{`.
 *
 * A plain textarea can't colour its own text, so this draws a backdrop layer
 * (the coloured text) behind a transparent-text textarea. Editing, caret and
 * selection stay fully native; the backdrop just paints underneath. Both layers
 * share identical box metrics and scroll in lockstep so glyphs line up 1:1.
 */
export function HighlightedTextarea({
  value,
  onChange,
  placeholder,
  readOnly,
  rows,
  autoFocus,
  className,
  variables,
  singleLine = false,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  rows?: number;
  autoFocus?: boolean;
  /** Extra classes applied to BOTH layers (e.g. a height for the popup). */
  className?: string;
  /** When present, typing `{{` opens a suggestion dropdown of these. */
  variables?: PromptVariable[];
  /** Keep compact fields like email subject to one clipped/scrollable line. */
  singleLine?: boolean;
}) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const html = useMemo(() => toHighlightedHtml(value), [value]);

  // Suggestion menu state.
  const [menu, setMenu] = useState<{ start: number; query: string; rect: DOMRect } | null>(null);
  const [menuIndex, setMenuIndex] = useState(0);

  const suggestions = useMemo(() => {
    if (!menu || !variables?.length) return [];
    const q = menu.query.trim().toLowerCase();
    if (!q) return variables;
    return variables.filter(
      (v) => v.label.toLowerCase().includes(q) || v.value.toLowerCase().includes(q),
    );
  }, [menu, variables]);

  const syncScroll = () => {
    const ta = taRef.current;
    const bd = backdropRef.current;
    if (ta && bd) {
      bd.scrollTop = ta.scrollTop;
      bd.scrollLeft = ta.scrollLeft;
    }
  };

  // Re-evaluate whether the suggestion menu should be open, based on the caret.
  const refreshMenu = () => {
    const ta = taRef.current;
    if (!ta || !variables?.length || readOnly) {
      setMenu(null);
      return;
    }
    const ctx = findVarContext(ta.value, ta.selectionStart ?? ta.value.length);
    if (!ctx) {
      setMenu(null);
      return;
    }
    setMenu({ ...ctx, rect: ta.getBoundingClientRect() });
    setMenuIndex(0);
  };

  const insertVariable = (v: PromptVariable) => {
    const ta = taRef.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? value.length;
    const ctx = findVarContext(value, caret);
    const start = ctx ? ctx.start : caret;
    const next = value.slice(0, start) + v.value + value.slice(caret);
    // Place the caret before a trailing "}}" scaffold (e.g. after "outputs.")
    // so the user can type the key; otherwise after the whole token.
    const target = v.value.endsWith(".}}")
      ? start + v.value.length - 2
      : start + v.value.length;
    onChange(next);
    setMenu(null);
    requestAnimationFrame(() => {
      const el = taRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(target, target);
      }
    });
  };

  // Identical metrics on both layers so the coloured glyphs sit exactly under
  // the caret. Keep this in sync between the backdrop div and the textarea.
  const box = cn(
    "w-full rounded-md border px-3 py-2 text-sm leading-5 font-sans",
    "whitespace-pre-wrap break-words",
  );

  const menuOpen = menu !== null && suggestions.length > 0;

  // Position the dropdown just under the field, flipping above if it would run
  // off the bottom of the viewport.
  const menuStyle: React.CSSProperties | null = menu
    ? (() => {
        const width = Math.min(menu.rect.width, 340);
        const below = menu.rect.bottom + 4;
        const flip = below + 240 > window.innerHeight;
        return {
          position: "fixed",
          left: menu.rect.left,
          width,
          ...(flip
            ? { bottom: window.innerHeight - menu.rect.top + 4 }
            : { top: below }),
        };
      })()
    : null;

  return (
    <div className={cn("relative", singleLine && "overflow-hidden rounded-md")}>
      <div
        ref={backdropRef}
        aria-hidden
        className={cn(
          box,
          "pointer-events-none absolute inset-0 overflow-y-auto overflow-x-hidden border-transparent text-text-primary",
          className,
        )}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      <textarea
        ref={taRef}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          refreshMenu();
        }}
        onKeyUp={(e) => {
          // Arrow keys / clicks move the caret without changing text.
          if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) refreshMenu();
        }}
        onClick={refreshMenu}
        onBlur={() => setMenu(null)}
        onScroll={syncScroll}
        onKeyDown={(e) => {
          if (!menuOpen) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setMenuIndex((i) => Math.min(i + 1, suggestions.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setMenuIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            insertVariable(suggestions[Math.min(menuIndex, suggestions.length - 1)]);
          } else if (e.key === "Escape") {
            e.preventDefault();
            setMenu(null);
          }
        }}
        placeholder={placeholder}
        readOnly={readOnly}
        rows={singleLine ? 2 : rows}
        autoFocus={autoFocus}
        spellCheck={false}
        style={{ caretColor: "var(--text-primary)" }}
        className={cn(
          box,
          "relative resize-none overflow-y-auto overflow-x-hidden border-border bg-transparent text-transparent outline-none transition-colors placeholder:text-text-muted focus:border-accent",
          readOnly && "cursor-not-allowed opacity-60",
          className,
        )}
      />
      {menuOpen &&
        menuStyle &&
        createPortal(
          <div
            style={menuStyle}
            className="z-[80] max-h-60 overflow-y-auto rounded-md border border-border bg-background py-1 shadow-lg"
            // Keep focus in the textarea; use mousedown so onBlur doesn't fire first.
            onMouseDown={(e) => e.preventDefault()}
          >
            {suggestions.map((v, i) => (
              <button
                key={v.value + i}
                type="button"
                onMouseEnter={() => setMenuIndex(i)}
                onClick={() => insertVariable(v)}
                className={cn(
                  "flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left transition-colors",
                  i === menuIndex ? "bg-surface-hover" : "hover:bg-surface-hover",
                )}
              >
                <span className="font-mono text-xs text-green-600 dark:text-green-400">
                  {v.value}
                </span>
                <span className="text-[11px] text-text-muted">
                  {v.label}
                  {v.hint ? ` · ${v.hint}` : ""}
                </span>
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
